import type { TriyoDynamicField, TriyoDynamicFieldFile } from "@/types/models";

/**
 * FieldType values the API sends, verbatim from Triyo.Web's FIELD_TYPES
 * (components/DynamicField/Tasks/utils/utils.js). The API returns these exact
 * display strings, so they are matched literally.
 */
export const DYNAMIC_FIELD_TYPES = {
  singleText: "Short Text Entry",
  multiText: "Long Text Entry",
  dropDown: "Dropdown",
  radioButtons: "Radio Buttons",
  checkbox: "Checkbox",
  date: "Date",
  time: "Time",
  integer: "Numeric (Integer)",
  decimal: "Numeric (Decimal)",
  table: "Table",
  toggle: "Toggle",
  file: "File",
  url: "URL",
  peoplePicker: "People Picker",
  multiSelectDropdown: "Multi-select Dropdown",
} as const;

/** FieldRegexDataType values that constrain the parsed value (Triyo.Web parity). */
const REGEX_DATA_TYPES = { int: "Integer", string: "String", number: "Decimal" } as const;

/** Toggle fields store these literal strings rather than a boolean. */
export const TOGGLE_ON = "selected";
export const TOGGLE_OFF = "not selected";

const COMMA_LIST_SEPARATOR = ", ";

function parseCommaListValue(data: string | null | undefined): string[] {
  return String(data ?? "")
    .split(COMMA_LIST_SEPARATOR)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function serializeCommaListValue(values: string[]): string {
  return values.filter(Boolean).join(COMMA_LIST_SEPARATOR);
}

/**
 * Table is excluded by Triyo.Web's project create/edit screen itself. File is
 * supported as a saved-file list so Deal details/edit can display, download,
 * validate, and round-trip its attachments without silently discarding them.
 */
const UNSUPPORTED_TYPES: string[] = [DYNAMIC_FIELD_TYPES.table];

/** Kendo upload statuses consumed by Triyo.API's DynamicFormRepository. */
export const DYNAMIC_FILE_STATUS = {
  initial: 1,
  uploaded: 4,
  removing: 6,
} as const;

/**
 * People Picker stores its value as a JSON array of UserIds, e.g. "[11,12]".
 * Non-integer entries are dropped, matching Triyo.Web's parsePeoplePickerIds.
 */
export function parsePeoplePickerIds(data: string | null | undefined): number[] {
  try {
    const parsed: unknown = JSON.parse(String(data ?? ""));
    return Array.isArray(parsed) ? parsed.filter((id): id is number => Number.isInteger(id)) : [];
  } catch {
    return [];
  }
}

export function serializePeoplePickerIds(ids: number[]): string {
  return JSON.stringify(ids);
}

export function isPeoplePickerField(field: TriyoDynamicField): boolean {
  return field.FieldType === DYNAMIC_FIELD_TYPES.peoplePicker;
}

export function parseMultiSelectDropdownValues(data: string | null | undefined): string[] {
  return parseCommaListValue(data);
}

export function serializeMultiSelectDropdownValues(values: string[]): string {
  return serializeCommaListValue(values);
}

export function isMultiSelectDropdownField(field: TriyoDynamicField): boolean {
  return field.FieldType === DYNAMIC_FIELD_TYPES.multiSelectDropdown;
}

export function isFileField(field: TriyoDynamicField): boolean {
  return field.FieldType === DYNAMIC_FIELD_TYPES.file;
}

/** Files that remain attached after the edit payload is saved. */
export function activeDynamicFieldFiles(files: TriyoDynamicFieldFile[] | null | undefined): TriyoDynamicFieldFile[] {
  return (files ?? []).filter(
    (file) => file.FileStatus !== DYNAMIC_FILE_STATUS.removing && file.status !== DYNAMIC_FILE_STATUS.removing
  );
}

export function dynamicFieldFileId(file: TriyoDynamicFieldFile): string | null {
  const id = file.Id || file.uid;
  return typeof id === "string" && id.trim() ? id : null;
}

export function dynamicFieldFileName(file: TriyoDynamicFieldFile, fallback = "Attachment"): string {
  const name = file.Name || file.name;
  return typeof name === "string" && name.trim() ? name : fallback;
}

export function parseCheckboxValue(data: string): string[] {
  return parseCommaListValue(data);
}

export function serializeCheckboxValue(values: string[]): string {
  return serializeCommaListValue(values);
}

/** True for the field types the add-in renders an input for. */
export function isSupportedField(field: TriyoDynamicField): boolean {
  return !UNSUPPORTED_TYPES.includes(field.FieldType ?? "");
}

/**
 * Fields shown on the Create Deal form: visible, not deleted, and of a type the
 * add-in can render — ordered by the admin-configured Order.
 */
export function visibleDynamicFields(fields: TriyoDynamicField[]): TriyoDynamicField[] {
  return fields
    .filter((field) => field.Visible !== false && field.Deleted !== true && isSupportedField(field))
    .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0));
}

/** Seeds each field with a non-null string Data, as Triyo.Web does after fetch. */
export function initializeDynamicFields(fields: TriyoDynamicField[]): TriyoDynamicField[] {
  return fields.map((field) => ({ ...field, Data: field.Data ?? "" }));
}

/**
 * Mirrors Triyo.Web isMaskValid: the value must match the admin-configured mask
 * position by position ('#' means digit, anything else is a literal).
 */
export function isMaskValid(field: TriyoDynamicField): boolean {
  const mask = field.FieldMask;
  if (!mask) return true;
  const data = String(field.Data ?? "");
  if (data.length !== mask.length) return false;
  for (let index = 0; index < mask.length; index += 1) {
    const maskChar = mask[index];
    const valueChar = data[index];
    if (maskChar === "#") {
      if (valueChar < "0" || valueChar > "9") return false;
    } else if (maskChar !== valueChar) {
      return false;
    }
  }
  return true;
}

/** Mirrors Triyo.Web isRegexValid: pattern test plus the declared data type. */
export function isRegexValid(field: TriyoDynamicField): boolean {
  const data = String(field.Data ?? "");
  if (field.FieldRegex) {
    try {
      if (!new RegExp(field.FieldRegex).test(data)) return false;
    } catch {
      // Some server-side .NET regex constructs are not valid JavaScript.
      // The API remains authoritative; do not block the user here.
    }
  }
  switch (field.FieldRegexDataType) {
    case REGEX_DATA_TYPES.int:
      return Number.isInteger(Number(data));
    case REGEX_DATA_TYPES.number:
      return !Number.isNaN(Number(data));
    default:
      return true;
  }
}

/** Mirrors Triyo.Web isFieldTypeValid: only Time carries a structural rule. */
export function isFieldTypeValid(field: TriyoDynamicField): boolean {
  if (field.FieldType !== DYNAMIC_FIELD_TYPES.time) return true;
  const [hours, minutes] = String(field.Data ?? "")
    .split(":")
    .map(Number);
  if ([hours, minutes].some((part) => !part && part !== 0)) return false;
  return hours >= 0 && minutes >= 0 && minutes <= 59;
}

/**
 * Validation message for one field, or null when it is acceptable. Mirrors
 * Triyo.Web isDynamicFieldValid: a required field must be non-blank AND pass
 * every check; an optional field is valid when blank, otherwise it must pass.
 */
export function validateDynamicField(field: TriyoDynamicField): string | null {
  if (isFileField(field)) {
    if (field.Required && activeDynamicFieldFiles(field.Files).length === 0) {
      return "At least one file is required.";
    }
    return null;
  }

  // "[]" is non-whitespace but still an EMPTY selection, so the generic checks
  // below would wrongly satisfy Required (Triyo.Web isDynamicFieldValid).
  if (isPeoplePickerField(field)) {
    if (field.Required && parsePeoplePickerIds(field.Data).length === 0) {
      return "Select at least one person.";
    }
    return null;
  }

  if (isMultiSelectDropdownField(field)) {
    if (field.Required && parseMultiSelectDropdownValues(field.Data).length === 0) {
      return "Select at least one option.";
    }
    return null;
  }

  const data = String(field.Data ?? "");
  // Triyo.Web treats only "" / null as empty; a whitespace-only value still has
  // to satisfy the mask and regex, so it is NOT short-circuited as blank here.
  const isEmpty = data === "";
  const isWhitespace = !/\S/.test(data);

  if (field.Required && (isEmpty || isWhitespace)) return "This field is required.";
  if (!field.Required && isEmpty) return null;

  if (!isMaskValid(field)) return `Must follow the format: ${field.FieldMask}`;
  if (!isFieldTypeValid(field)) return "Use HH:MM (for example, 09:30).";
  if (!isRegexValid(field)) {
    return field.FieldRegexDescription || "The value does not match the required format.";
  }
  return null;
}

/** Validation messages keyed by field Id; empty when every field is acceptable. */
export function validateDynamicFields(fields: TriyoDynamicField[]): Record<number, string> {
  const errors: Record<number, string> = {};
  for (const field of visibleDynamicFields(fields)) {
    const message = validateDynamicField(field);
    if (message) errors[field.Id] = message;
  }
  return errors;
}

/** One dynamicFields entry on the AddProject body. */
export interface DealDynamicFieldPayload {
  DynamicFieldId: number;
  Data: string;
  ObjectId: number;
  Files?: TriyoDynamicFieldFile[];
}

/**
 * Maps the form state onto the AddProject `dynamicFields` array, matching the
 * shape Triyo.Web's project-form sends. Table fields remain excluded; File
 * fields keep their original Files collection so an unrelated Deal edit cannot
 * remove existing attachments.
 */
export function buildDynamicFieldsPayload(fields: TriyoDynamicField[], objectId = 0): DealDynamicFieldPayload[] {
  return (
    fields
      .filter(isSupportedField)
      // The API's SaveDynamicFields aborts the whole list (a `return`, not a
      // `continue`) on the first entry whose DynamicFieldId is not > 0, so an
      // id-less field would silently discard every field after it.
      .filter((field) => Number(field.Id) > 0)
      .map((field) => ({
        DynamicFieldId: field.Id,
        Data: String(field.Data ?? ""),
        // Always present but meaningless on create: AddProject overwrites it with
        // the new project id, and the DTO marks it [Required].
        ObjectId: objectId,
        ...(field.Files ? { Files: field.Files } : {}),
      }))
  );
}
